import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import { motion } from 'framer-motion';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Textarea } from './components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Badge } from './components/ui/badge';
import { Toaster, toast } from './components/ui/sonner';
import { 
  Leaf, 
  Recycle, 
  Sun, 
  Droplets, 
  Wind, 
  CheckCircle2, 
  ArrowRight, 
  Phone, 
  Mail, 
  MapPin,
  Factory,
  Shield,
  FileCheck,
  TrendingUp,
  Users,
  Zap,
  Play,
  Award,
  Target,
  Handshake,
  BarChart3,
  Sparkles,
  ChevronRight,
  DollarSign,
  Globe,
  Briefcase,
  LineChart,
  PieChart,
  Calendar,
  Star,
  Quote,
  Building2,
  Rocket,
  TrendingDown,
  ArrowUp,
  Lightbulb
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function App() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    honeypot: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const videoRef = useRef(null);
  const avsfButtonRef = useRef(null);
  const solarButtonRef = useRef(null);

  // Calculator State
  const [avsfInputs, setAvsfInputs] = useState({
    vehicleType: 'personal',
    exShowroomPrice: ''
  });
  const [solarInputs, setSolarInputs] = useState({
    landSize: '',
    investmentModel: '8020'
  });
  const [avsfResults, setAvsfResults] = useState(null);
  const [solarResults, setSolarResults] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);


  // Manually attach calculator button event listeners as fallback
  useEffect(() => {
    if (avsfButtonRef.current) {
      const handleAVSFClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        calculateAVSF();
      };
      avsfButtonRef.current.addEventListener('click', handleAVSFClick);
      return () => avsfButtonRef.current?.removeEventListener('click', handleAVSFClick);
    }
  }, [avsfInputs]);

  useEffect(() => {
    if (solarButtonRef.current) {
      const handleSolarClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        calculateSolar();
      };
      solarButtonRef.current.addEventListener('click', handleSolarClick);
      return () => solarButtonRef.current?.removeEventListener('click', handleSolarClick);
    }
  }, [solarInputs]);


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Message Sent!', {
          description: data.message || 'Thank you for your message. We\'ll get back to you soon!',
          duration: 5000,
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
          honeypot: ''
        });
      } else {
        toast.error('Submission Failed', {
          description: data.detail || 'Failed to submit form. Please try again.',
          duration: 5000,
        });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Network Error', {
        description: 'Please check your connection and try again.',
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // AVSF Calculator Function
  const calculateAVSF = () => {
    const price = parseFloat(avsfInputs.exShowroomPrice);
    if (!price || price <= 0) {
      toast.error('Invalid Input', {
        description: 'Please enter a valid ex-showroom price',
        duration: 3000
      });
      return;
    }

    const scrapCertMin = Math.round(price * 0.04);
    const scrapCertMax = Math.round(price * 0.06);
    const roadTaxMin = 15000;
    const roadTaxMax = 40000;
    const manufacturerMin = Math.round(price * 0.03);
    const manufacturerMax = Math.round(price * 0.05);
    const registrationMin = 5000;
    const registrationMax = 10000;

    const totalMin = scrapCertMin + roadTaxMin + manufacturerMin + registrationMin;
    const totalMax = scrapCertMax + roadTaxMax + manufacturerMax + registrationMax;

    setAvsfResults({
      scrapCert: `₹${scrapCertMin.toLocaleString('en-IN')}-${scrapCertMax.toLocaleString('en-IN')}`,
      roadTax: `₹${roadTaxMin.toLocaleString('en-IN')}-${roadTaxMax.toLocaleString('en-IN')}`,
      manufacturer: `₹${manufacturerMin.toLocaleString('en-IN')}-${manufacturerMax.toLocaleString('en-IN')}`,
      registration: `₹${registrationMin.toLocaleString('en-IN')}-${registrationMax.toLocaleString('en-IN')}`,
      total: `₹${totalMin.toLocaleString('en-IN')}-${totalMax.toLocaleString('en-IN')}`
    });

    toast.success('Calculation Complete!', {
      description: `Total benefits: ₹${totalMin.toLocaleString('en-IN')}-${totalMax.toLocaleString('en-IN')}`,
      duration: 5000
    });
  };

  // Solar Calculator Function
  const calculateSolar = () => {
    const acres = parseFloat(solarInputs.landSize);
    if (!acres || acres <= 0) {
      toast.error('Invalid Input', {
        description: 'Please enter a valid land size in acres',
        duration: 3000
      });
      return;
    }

    const capacityPerAcre = 0.25; // MW per acre
    const totalCapacity = acres * capacityPerAcre;
    const costPerMW = 4.2; // Crores
    const totalCost = totalCapacity * costPerMW;
    
    let investment, annualIncome, roi, totalReturn;
    
    if (solarInputs.investmentModel === '8020') {
      investment = totalCost * 0.20;
      annualIncome = totalCapacity * 0.15; // 15 lakhs per MW per year
      roi = (annualIncome / investment) * 100;
      totalReturn = annualIncome * 25; // 25 years
    } else if (solarInputs.investmentModel === 'resco') {
      investment = 0;
      annualIncome = acres * 0.50; // 50k per acre as lease
      roi = Infinity;
      totalReturn = annualIncome * 25;
    } else { // full
      investment = totalCost;
      annualIncome = totalCapacity * 0.90; // 90 lakhs per MW per year
      roi = (annualIncome / investment) * 100;
      totalReturn = annualIncome * 25;
    }

    const newResults = {
      capacity: `${totalCapacity.toFixed(2)} MW (${acres} acres)`,
      investment: investment === 0 ? 'Zero Investment' : `₹${investment.toFixed(2)} Cr`,
      annualIncome: `₹${annualIncome.toFixed(2)} ${investment === 0 ? 'Lakhs' : 'Cr'}`,
      roi: roi === Infinity ? 'Infinite (Lease Model)' : `${roi.toFixed(1)}%`,
      payback: roi === Infinity ? 'N/A' : `${(100/roi).toFixed(1)} years`,
      totalReturn: `₹${(totalReturn).toFixed(2)} Cr`
    };
    
    setSolarResults(newResults);

    toast.success('Calculation Complete!', {
      description: `Annual income: ₹${annualIncome.toFixed(2)} ${investment === 0 ? 'Lakhs' : 'Cr'}`,
      duration: 5000
    });
  };


  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header - Premium & Professional */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
          isScrolled ? 'bg-card/98 backdrop-blur-lg shadow-md border-b border-border' : 'bg-transparent'
        }`}
        data-testid="header"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-20 md:h-24 flex items-center justify-between gap-4">
            {/* Logo - Prominent sizing for brand presence */}
            <div className="flex items-center" data-testid="logo">
              <img 
                src="/esg-logo-transparent.png" 
                alt="ESG Solutions - Renewable Energy, Changing Lives" 
                className="h-16 md:h-20 w-auto max-w-[260px] md:max-w-[380px] object-contain"
                style={{ filter: 'drop-shadow(0 3px 12px rgba(44, 90, 75, 0.25))' }}
              />
            </div>
            <nav className="hidden md:flex items-center space-x-8" data-testid="nav">
              <button onClick={() => scrollToSection('home')} className="text-sm font-medium hover:text-primary transition-colors" data-testid="nav-home">
                Home
              </button>
              <button onClick={() => scrollToSection('services')} className="text-sm font-medium hover:text-primary transition-colors" data-testid="nav-services">
                Services
              </button>
              <button onClick={() => scrollToSection('calculators')} className="text-sm font-medium hover:text-primary transition-colors" data-testid="nav-calculators">
                Calculators
              </button>
              <button onClick={() => scrollToSection('policies')} className="text-sm font-medium hover:text-primary transition-colors" data-testid="nav-policies">
                Gov. Policies
              </button>
              <button onClick={() => scrollToSection('about')} className="text-sm font-medium hover:text-primary transition-colors" data-testid="nav-about">
                About
              </button>
              <button onClick={() => scrollToSection('contact')} className="text-sm font-medium hover:text-primary transition-colors" data-testid="nav-contact">
                Contact
              </button>
            </nav>
            <Button 
              onClick={() => scrollToSection('contact')} 
              className="hidden md:inline-flex shadow-lg"
              data-testid="header-cta"
            >
              Get Started <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section with Animated Background */}
      <section id="home" className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
        {/* Animated Background - Professional landscape with windmills */}
        <div className="absolute inset-0 z-0 bg-gradient-to-b from-[#f5f7f5] via-[#e8ede8] to-[#d8e3db]">
          {/* Mountain silhouettes - faded in background */}
          <div className="hero-animated-bg">
            {/* Distant mountains - very faded */}
            <svg className="mountain-layer mountain-far" viewBox="0 0 1200 400" preserveAspectRatio="none">
              <path d="M0,300 L200,180 L400,220 L600,140 L800,200 L1000,160 L1200,240 L1200,400 L0,400 Z" 
                    fill="rgba(44, 90, 75, 0.06)" />
            </svg>
            
            {/* Mid-range mountains - slightly more visible */}
            <svg className="mountain-layer mountain-mid" viewBox="0 0 1200 400" preserveAspectRatio="none">
              <path d="M0,320 L150,240 L350,280 L550,200 L750,260 L950,220 L1200,280 L1200,400 L0,400 Z" 
                    fill="rgba(44, 90, 75, 0.1)" />
            </svg>
            
            {/* Closer mountains - more defined */}
            <svg className="mountain-layer mountain-near" viewBox="0 0 1200 400" preserveAspectRatio="none">
              <path d="M0,340 L180,280 L380,310 L580,260 L780,300 L980,270 L1200,310 L1200,400 L0,400 Z" 
                    fill="rgba(44, 90, 75, 0.15)" />
            </svg>
            
            {/* Windmill 1 - Large Left */}
            <div className="windmill windmill-1">
              <div className="windmill-tower"></div>
              <div className="windmill-blades">
                <div className="blade blade-1"></div>
                <div className="blade blade-2"></div>
                <div className="blade blade-3"></div>
              </div>
            </div>
            
            {/* Windmill 2 - Right */}
            <div className="windmill windmill-2">
              <div className="windmill-tower"></div>
              <div className="windmill-blades blades-fast">
                <div className="blade blade-1"></div>
                <div className="blade blade-2"></div>
                <div className="blade blade-3"></div>
              </div>
            </div>
            
            {/* Windmill 3 - Small Far */}
            <div className="windmill windmill-3">
              <div className="windmill-tower"></div>
              <div className="windmill-blades blades-slow">
                <div className="blade blade-1"></div>
                <div className="blade blade-2"></div>
                <div className="blade blade-3"></div>
              </div>
            </div>
          </div>
          
          {/* Subtle overlay to ensure text readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-background/30 via-background/15 to-background/25"></div>
        </div>
        
        {/* Video Background (Desktop only) */}
        <div className="absolute inset-0 z-[1] hero-video-container hidden md:block">
          <div className="absolute inset-0 bg-gradient-to-br from-background/95 via-background/90 to-background/85 z-10"></div>
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            className="w-full h-full object-cover"
            poster="https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=1920&q=80"
            style={{ minHeight: '100vh' }}
            onLoadedData={() => {
              if (videoRef.current) {
                videoRef.current.play().catch(e => {
                  console.log('Video autoplay prevented:', e);
                });
              }
            }}
          >
            {/* Using Pexels free stock video - Solar panels and renewable energy */}
            <source src="https://videos.pexels.com/video-files/7710243/7710243-uhd_2560_1440_30fps.mp4" type="video/mp4" />
            <source src="https://videos.pexels.com/video-files/3129671/3129671-uhd_2560_1440_30fps.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>

        {/* Hero Content - Conversion-Optimized, Above the fold */}
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Hero Text */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Badge */}
              <div className="inline-flex items-center space-x-2 mb-6 px-5 py-3 rounded-full bg-primary/15 border-2 border-primary/30" data-testid="hero-badge">
                <Sparkles className="h-5 w-5 text-primary" />
                <span className="text-base md:text-lg font-bold text-primary">Pioneering Renewable Infrastructure</span>
              </div>

              {/* Headline - Bold, clear, powerful */}
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.1] mb-6 md:mb-8 text-foreground" data-testid="hero-title">
                Trusted Advisors in India's Green Energy Transformation
              </h1>

              {/* Subheadline - Concise value proposition */}
              <p className="text-lg md:text-xl lg:text-2xl text-muted-foreground leading-relaxed mb-10 md:mb-12 max-w-3xl" data-testid="hero-subtitle">
                ESG Solutions partners with landowners, industries, and investors to develop solar energy projects and automated vehicle scrapping infrastructure that creates long-term environmental and financial value.
              </p>

              {/* CTAs - Premium, high-conversion */}
              <div className="flex flex-col sm:flex-row gap-4" data-testid="hero-ctas">
                <Button 
                  size="lg" 
                  className="text-base md:text-lg px-8 py-5 shadow-xl hover:shadow-2xl font-semibold h-auto"
                  onClick={() => scrollToSection('contact')}
                  data-testid="hero-cta-primary"
                >
                  Partner With ESG <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="text-base md:text-lg px-8 py-5 shadow-lg hover:shadow-xl font-semibold border-2 h-auto"
                  onClick={() => scrollToSection('contact')}
                  data-testid="hero-cta-secondary"
                >
                  Book a Consultation
                </Button>
              </div>
            </motion.div>

            {/* Right: Hero Visual - Premium Infrastructure Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1762381157076-4872b31961e0?w=1200&q=80"
                  alt="Wind turbines and solar panels - ESG Solutions renewable infrastructure"
                  className="w-full h-[600px] object-cover"
                  data-testid="hero-infrastructure-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent"></div>
                
                {/* Stats Overlay Card */}
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="bg-background/95 backdrop-blur-md rounded-2xl p-6 shadow-xl border-2 border-primary/20">
                    <div className="grid grid-cols-2 gap-6">
                      <div className="text-center border-r border-border">
                        <div className="text-3xl font-bold text-primary mb-1">500 GW</div>
                        <div className="text-xs text-muted-foreground font-medium">India's 2030 Target</div>
                      </div>
                      <div className="text-center">
                        <div className="text-3xl font-bold text-primary mb-1">₹7.2L Cr</div>
                        <div className="text-xs text-muted-foreground font-medium">AVSF Opportunity</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust Strip - Value Pillars */}
      <section className="py-10 md:py-12 bg-card border-y-2 border-border" data-testid="trust-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
            <div className="flex flex-col items-center text-center" data-testid="trust-item-1">
              <div className="h-14 w-14 md:h-16 md:w-16 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                <Sun className="h-7 w-7 md:h-9 md:w-9 text-primary" />
              </div>
              <h3 className="font-bold text-base md:text-lg">Solar Development</h3>
            </div>
            <div className="flex flex-col items-center text-center" data-testid="trust-item-2">
              <div className="h-14 w-14 md:h-16 md:w-16 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                <Factory className="h-7 w-7 md:h-9 md:w-9 text-primary" />
              </div>
              <h3 className="font-bold text-base md:text-lg">AVSF Infrastructure</h3>
            </div>
            <div className="flex flex-col items-center text-center" data-testid="trust-item-3">
              <div className="h-14 w-14 md:h-16 md:w-16 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                <Handshake className="h-7 w-7 md:h-9 md:w-9 text-primary" />
              </div>
              <h3 className="font-bold text-base md:text-lg">Land Partnerships</h3>
            </div>
            <div className="flex flex-col items-center text-center" data-testid="trust-item-4">
              <div className="h-14 w-14 md:h-16 md:w-16 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                <Shield className="h-7 w-7 md:h-9 md:w-9 text-primary" />
              </div>
              <h3 className="font-bold text-base md:text-lg">ESG Advisory</h3>
            </div>
          </div>
        </div>
      </section>

      {/* Why ESG Solutions - Premium Feature Cards */}
      <section className="py-20 md:py-28" data-testid="why-esg-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 text-sm px-5 py-2.5">Why Partner With Us</Badge>
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground mb-6">
              Why ESG Solutions
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
              Sustainable infrastructure partnerships that create measurable value
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 md:gap-10">
            {/* Land Monetization */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Card className="premium-card h-full border-2 hover:border-primary/40" data-testid="why-card-1">
                <CardContent className="p-8 md:p-10">
                  <div className="h-16 w-16 md:h-20 md:w-20 rounded-2xl bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center mb-6 shadow-lg">
                    <DollarSign className="h-8 w-8 md:h-10 md:w-10 text-primary-foreground" />
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold mb-4">Land Monetization</h3>
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                    We help unlock long-term income opportunities through renewable infrastructure partnerships.
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            {/* Renewable Infrastructure */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <Card className="premium-card h-full border-2 hover:border-primary/40" data-testid="why-card-2">
                <CardContent className="p-8 md:p-10">
                  <div className="h-16 w-16 md:h-20 md:w-20 rounded-2xl bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center mb-6 shadow-lg">
                    <Zap className="h-8 w-8 md:h-10 md:w-10 text-primary-foreground" />
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold mb-4">Renewable Infrastructure</h3>
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                    From solar facilitation to sustainable project planning, we build practical pathways for growth.
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            {/* Environmental Impact */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Card className="premium-card h-full border-2 hover:border-primary/40" data-testid="why-card-3">
                <CardContent className="p-8 md:p-10">
                  <div className="h-16 w-16 md:h-20 md:w-20 rounded-2xl bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center mb-6 shadow-lg">
                    <Leaf className="h-8 w-8 md:h-10 md:w-10 text-primary-foreground" />
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold mb-4">Environmental Impact</h3>
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                    Our solutions combine business opportunity with measurable sustainability outcomes.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section - Premium Presentation */}
      <section className="py-20 md:py-28 bg-primary/5" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 text-sm px-5 py-2.5">Market Opportunity</Badge>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-foreground mb-4">
              India's Renewable Revolution
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              className="text-center p-8 md:p-10 rounded-2xl bg-card border-2 border-primary/20 shadow-xl hover:shadow-2xl transition-shadow duration-300"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              data-testid="stat-card-1"
            >
              <div className="text-5xl md:text-6xl font-bold text-primary mb-4">₹7.2L+</div>
              <div className="text-lg md:text-xl font-bold text-foreground mb-2">AVSF Opportunity</div>
              <div className="text-sm md:text-base text-muted-foreground">Per eligible vehicle</div>
            </motion.div>

            <motion.div
              className="text-center p-8 md:p-10 rounded-2xl bg-card border-2 border-primary/20 shadow-xl hover:shadow-2xl transition-shadow duration-300"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              data-testid="stat-card-2"
            >
              <div className="text-5xl md:text-6xl font-bold text-primary mb-4">209 GW</div>
              <div className="text-lg md:text-xl font-bold text-foreground mb-2">Renewable Potential</div>
              <div className="text-sm md:text-base text-muted-foreground">Current capacity</div>
            </motion.div>

            <motion.div
              className="text-center p-8 md:p-10 rounded-2xl bg-card border-2 border-primary/20 shadow-xl hover:shadow-2xl transition-shadow duration-300"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              data-testid="stat-card-3"
            >
              <div className="text-5xl md:text-6xl font-bold text-primary mb-4">500 GW</div>
              <div className="text-lg md:text-xl font-bold text-foreground mb-2">India Target</div>
              <div className="text-sm md:text-base text-muted-foreground">By 2030</div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* About / Value Proposition */}
      <section id="about" className="py-20 md:py-28" data-testid="value-prop-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <Badge className="mb-6 text-sm px-5 py-2.5">About Us</Badge>
              <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground mb-8">
                At the Intersection of Sustainability and Commercial Opportunity
              </h2>
              <div className="space-y-6 text-lg md:text-xl text-muted-foreground leading-relaxed">
                <p>
                  We are building renewable and environmental infrastructure partnerships across India.
                </p>
                <p>
                  Our approach combines practical project execution with ambitious long-term vision, creating scalable solutions that deliver both environmental impact and financial returns.
                </p>
                <p>
                  From facilitating solar energy developments to operating India's first Automated Vehicle Scrapping Facility in Panipat, we focus on infrastructure that matters.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Overview - Clean Cards */}
      <section id="services" className="py-16 md:py-20 bg-muted/40" data-testid="services-overview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 text-sm px-5 py-2.5">Our Services</Badge>
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground mb-6">
              Comprehensive Environmental Infrastructure
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
              From solar facilitation to vehicle scrapping, we deliver end-to-end renewable infrastructure solutions.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Service 1 - Solar */}
            <Card className="premium-card hover:shadow-2xl" data-testid="service-overview-solar">
              <CardContent className="p-8 md:p-10">
                <div className="flex items-start gap-5 mb-6">
                  <div className="h-16 w-16 md:h-18 md:w-18 rounded-xl bg-primary/15 flex items-center justify-center flex-shrink-0">
                    <Sun className="h-8 w-8 md:h-10 md:w-10 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-2xl md:text-3xl font-bold mb-3">Solar Energy Facilitation</h3>
                    <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                      End-to-end solar infrastructure development with 80/20 co-investment model. We handle setup, operations, and grid connectivity for 25+ year partnerships.
                    </p>
                  </div>
                </div>
                <Button variant="outline" size="lg" onClick={() => scrollToSection('calculators')} className="w-full md:w-auto" data-testid="service-solar-cta">
                  Calculate ROI <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </CardContent>
            </Card>

            {/* Service 2 - AVSF */}
            <Card className="premium-card hover:shadow-2xl" data-testid="service-overview-avsf">
              <CardContent className="p-8 md:p-10">
                <div className="flex items-start gap-5 mb-6">
                  <div className="h-16 w-16 md:h-18 md:w-18 rounded-xl bg-primary/15 flex items-center justify-center flex-shrink-0">
                    <Recycle className="h-8 w-8 md:h-10 md:w-10 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-2xl md:text-3xl font-bold mb-3">Automated Vehicle Scrapping Facility</h3>
                    <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                      Government-registered AVSF in Panipat offering up to ₹7.2L+ benefits per vehicle through VVMP compliance and incentives.
                    </p>
                  </div>
                </div>
                <Button variant="outline" size="lg" onClick={() => scrollToSection('calculators')} className="w-full md:w-auto" data-testid="service-avsf-cta">
                  Calculate Benefits <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </CardContent>
            </Card>

            {/* Service 3 - Partnerships */}
            <Card className="premium-card hover:shadow-2xl" data-testid="service-overview-partnerships">
              <CardContent className="p-8 md:p-10">
                <div className="flex items-start gap-5 mb-6">
                  <div className="h-16 w-16 md:h-18 md:w-18 rounded-xl bg-primary/15 flex items-center justify-center flex-shrink-0">
                    <Handshake className="h-8 w-8 md:h-10 md:w-10 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-2xl md:text-3xl font-bold mb-3">Landowner Partnership Models</h3>
                    <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                      Transform idle land into income-generating renewable infrastructure through flexible partnership structures and shared investment models.
                    </p>
                  </div>
                </div>
                <Button variant="outline" size="lg" onClick={() => scrollToSection('contact')} className="w-full md:w-auto" data-testid="service-partnership-cta">
                  Explore Options <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </CardContent>
            </Card>

            {/* Service 4 - Advisory */}
            <Card className="premium-card hover:shadow-2xl" data-testid="service-overview-advisory">
              <CardContent className="p-8 md:p-10">
                <div className="flex items-start gap-5 mb-6">
                  <div className="h-16 w-16 md:h-18 md:w-18 rounded-xl bg-primary/15 flex items-center justify-center flex-shrink-0">
                    <Lightbulb className="h-8 w-8 md:h-10 md:w-10 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-2xl md:text-3xl font-bold mb-3">Environmental Infrastructure Advisory</h3>
                    <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                      Strategic guidance for ESG compliance, project feasibility, regulatory navigation, and sustainable infrastructure planning.
                    </p>
                  </div>
                </div>
                <Button variant="outline" size="lg" onClick={() => scrollToSection('contact')} className="w-full md:w-auto" data-testid="service-advisory-cta">
                  Learn More <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>


      {/* Detailed Services with Visual Slideshows - "How We Work" */}
      <section className="py-20 md:py-28 bg-background" data-testid="services-detailed-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 text-sm px-5 py-2.5">How We Work</Badge>
            <h2 className="text-4xl sm:text-5xl font-bold tracking-tight text-foreground mb-6">
              See Our Services in Action
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
              Visual insights into our operations, infrastructure, and partnership processes
            </p>
          </div>

          {/* AVSF Service - Large Feature with Slideshow */}
          <div className="mb-20">
            <Card className="overflow-hidden border-2 hover:shadow-2xl transition-shadow duration-300" data-testid="service-detail-avsf">
              <div className="grid md:grid-cols-2 gap-0">
                <div className="relative h-[400px] md:h-auto">
                  {/* AVSF Animated Slideshow */}
                  <div className="slideshow-container">
                    <img
                      src="https://images.unsplash.com/photo-1770068511830-a6cc498f4bfe?w=800&q=80"
                      alt="Vehicle scrap yard aerial view"
                      className="slideshow-image"
                    />
                    <img
                      src="https://images.unsplash.com/photo-1727373203641-7615f95d3bd1?w=800&q=80"
                      alt="Industrial processing equipment"
                      className="slideshow-image"
                    />
                    <img
                      src="https://images.unsplash.com/photo-1604501997347-025aeb7d4479?w=800&q=80"
                      alt="Vehicle dismantling process"
                      className="slideshow-image"
                    />
                    <img
                      src="https://images.unsplash.com/photo-1717667745830-de42bb75a4fa?w=800&q=80"
                      alt="Material recovery operations"
                      className="slideshow-image"
                    />
                    <div className="slideshow-overlay"></div>
                    <div className="slideshow-text">
                      <span className="step-indicator">Live Operations</span>
                      <h3 className="slideshow-title">Panipat AVSF Facility</h3>
                      <p className="slideshow-description">
                        Professional vehicle scrapping with modern equipment and 100% compliance
                      </p>
                    </div>
                  </div>
                </div>
                <CardContent className="p-8 md:p-12">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="h-14 w-14 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Recycle className="h-7 w-7 text-primary" />
                    </div>
                    <div>
                      <Badge variant="outline" className="mb-2">Active Service</Badge>
                      <CardTitle className="text-3xl" data-testid="avsf-detail-title">Automated Vehicle Scrap Facility</CardTitle>
                    </div>
                  </div>
                  
                  <div className="mb-6 p-4 rounded-xl bg-accent/30 border border-accent/50">
                    <p className="text-sm font-semibold text-accent-foreground flex items-center">
                      <MapPin className="h-4 w-4 mr-2" />
                      Facility Location: Panipat, Haryana
                    </p>
                  </div>

                  <p className="text-base text-muted-foreground mb-6 leading-relaxed">
                    Our state-of-the-art facility in Panipat specializes in automated vehicle scrapping with modern equipment, complete compliance, and environmental responsibility.
                  </p>

                  <div className="space-y-4 mb-8">
                    <div className="flex items-start" data-testid="avsf-detail-feature-1">
                      <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                        <CheckCircle2 className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-semibold mb-1">Modern Equipment</h4>
                        <p className="text-sm text-muted-foreground">Automated vehicle scrapping with cutting-edge technology and safety protocols</p>
                      </div>
                    </div>
                    <div className="flex items-start" data-testid="avsf-detail-feature-2">
                      <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                        <CheckCircle2 className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-semibold mb-1">Complete Processing</h4>
                        <p className="text-sm text-muted-foreground">End-of-life vehicle dismantling and material recovery with maximum efficiency</p>
                      </div>
                    </div>
                    <div className="flex items-start" data-testid="avsf-detail-feature-3">
                      <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                        <CheckCircle2 className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-semibold mb-1">Environmental Care</h4>
                        <p className="text-sm text-muted-foreground">Responsible disposal practices meeting all environmental standards</p>
                      </div>
                    </div>
                    <div className="flex items-start" data-testid="avsf-detail-feature-4">
                      <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                        <CheckCircle2 className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-semibold mb-1">Full Documentation</h4>
                        <p className="text-sm text-muted-foreground">Complete compliance support and transparent reporting</p>
                      </div>
                    </div>
                  </div>

                  <Button size="lg" onClick={() => scrollToSection('contact')} data-testid="avsf-detail-cta" className="w-full md:w-auto">
                    Learn More About AVSF <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </div>
            </Card>
          </div>

          {/* Solar Farming Service - Large Feature with Slideshow */}
          <div>
            <Card className="overflow-hidden border-2 border-dashed hover:shadow-2xl transition-shadow duration-300" data-testid="service-detail-solar">
              <div className="grid md:grid-cols-2 gap-0">
                <CardContent className="p-8 md:p-12 order-2 md:order-1">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="h-14 w-14 rounded-xl bg-accent flex items-center justify-center">
                      <Sun className="h-7 w-7 text-accent-foreground" />
                    </div>
                    <div>
                      <Badge className="mb-2 bg-accent text-accent-foreground">Coming Soon</Badge>
                      <CardTitle className="text-3xl" data-testid="solar-detail-title">Solar Farming Partnerships</CardTitle>
                    </div>
                  </div>

                  <div className="mb-6 p-4 rounded-xl bg-accent/20 border border-accent/30">
                    <p className="text-sm font-semibold text-accent-foreground">
                      Partner with us to transform your land into a sustainable income source through renewable energy
                    </p>
                  </div>

                  <p className="text-base text-muted-foreground mb-6 leading-relaxed">
                    Our innovative co-investment model helps landowners set up complete solar farm infrastructure, connecting them directly with electricity-consuming facilities for regular, sustainable income.
                  </p>

                  <div className="space-y-4 mb-8">
                    <div className="flex items-start" data-testid="solar-detail-feature-1">
                      <div className="h-6 w-6 rounded-full bg-accent/30 flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                        <CheckCircle2 className="h-4 w-4 text-accent-foreground" />
                      </div>
                      <div>
                        <h4 className="font-semibold mb-1">Co-Investment Model</h4>
                        <p className="text-sm text-muted-foreground">Strategic partnership where we invest alongside landowners</p>
                      </div>
                    </div>
                    <div className="flex items-start" data-testid="solar-detail-feature-2">
                      <div className="h-6 w-6 rounded-full bg-accent/30 flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                        <CheckCircle2 className="h-4 w-4 text-accent-foreground" />
                      </div>
                      <div>
                        <h4 className="font-semibold mb-1">Complete Setup</h4>
                        <p className="text-sm text-muted-foreground">Full solar farm infrastructure installation and ongoing management</p>
                      </div>
                    </div>
                    <div className="flex items-start" data-testid="solar-detail-feature-3">
                      <div className="h-6 w-6 rounded-full bg-accent/30 flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                        <CheckCircle2 className="h-4 w-4 text-accent-foreground" />
                      </div>
                      <div>
                        <h4 className="font-semibold mb-1">Direct Tie-Ups</h4>
                        <p className="text-sm text-muted-foreground">Guaranteed connections with electricity-consuming facilities</p>
                      </div>
                    </div>
                    <div className="flex items-start" data-testid="solar-detail-feature-4">
                      <div className="h-6 w-6 rounded-full bg-accent/30 flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                        <CheckCircle2 className="h-4 w-4 text-accent-foreground" />
                      </div>
                      <div>
                        <h4 className="font-semibold mb-1">Regular Income</h4>
                        <p className="text-sm text-muted-foreground">Continuous revenue generation through renewable energy sales</p>
                      </div>
                    </div>
                  </div>

                  <Button size="lg" onClick={() => scrollToSection('contact')} variant="outline" data-testid="solar-detail-cta" className="w-full md:w-auto">
                    Pre-Register Interest <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
                <div className="relative h-[400px] md:h-auto order-1 md:order-2">
                  {/* Solar Partnership Animated Slideshow */}
                  <div className="slideshow-container">
                    <img
                      src="https://images.unsplash.com/photo-1713544123617-449320086fcd?w=800&q=80"
                      alt="Large scale solar farm"
                      className="slideshow-image"
                    />
                    <img
                      src="https://images.unsplash.com/photo-1630672607721-48e0a0187a92?w=800&q=80"
                      alt="Solar panels on grassland"
                      className="slideshow-image"
                    />
                    <img
                      src="https://images.unsplash.com/photo-1713544123590-d5b03390bbd8?w=800&q=80"
                      alt="Solar panel installation"
                      className="slideshow-image"
                    />
                    <img
                      src="https://images.unsplash.com/photo-1715605569625-1ec3b5624a9a?w=800&q=80"
                      alt="Solar infrastructure setup"
                      className="slideshow-image"
                    />
                    <div className="slideshow-overlay"></div>
                    <div className="slideshow-text">
                      <span className="step-indicator">Coming Soon</span>
                      <h3 className="slideshow-title">Solar Partnership Model</h3>
                      <p className="slideshow-description">
                        80% ESG investment • 20% Landowner • Guaranteed income for 25 years
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>


      {/* Interactive Calculators Section */}
      <section id="calculators" className="py-16 md:py-20 bg-muted/30" data-testid="calculators-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <Badge className="mb-4 text-sm px-4 py-2">Interactive Tools</Badge>
            <h2 className="text-4xl sm:text-5xl font-bold tracking-tight text-foreground mb-6">
              Calculate Your Benefits & Returns
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
              Use our calculators to understand potential savings from vehicle scrapping and returns from solar farming partnerships.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* AVSF Benefit Calculator */}
            <Card className="premium-card">
              <CardHeader>
                <div className="flex items-center justify-between mb-4">
                  <Badge>AVSF Calculator</Badge>
                  <Recycle className="h-6 w-6 text-primary" />
                </div>
                <CardTitle className="text-2xl mb-2">Vehicle Scrapping Benefits</CardTitle>
                <CardDescription>Calculate total savings from scrapping your old vehicle</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold mb-2">Vehicle Type</label>
                  <select 
                    className="w-full p-3 border rounded-lg bg-background" 
                    data-testid="vehicle-type"
                    value={avsfInputs.vehicleType}
                    onChange={(e) => setAvsfInputs({...avsfInputs, vehicleType: e.target.value})}
                  >
                    <option value="personal">Personal Vehicle (20+ years)</option>
                    <option value="commercial">Commercial Vehicle (15+ years)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Ex-Showroom Price (when new) ₹</label>
                  <Input 
                    type="number" 
                    placeholder="e.g., 600000" 
                    className="text-lg" 
                    data-testid="ex-showroom-price"
                    value={avsfInputs.exShowroomPrice}
                    onChange={(e) => setAvsfInputs({...avsfInputs, exShowroomPrice: e.target.value})}
                  />
                </div>
                <button
                  ref={avsfButtonRef}
                  type="button"
                  className="w-full h-12 px-8 bg-primary text-primary-foreground rounded-lg font-bold text-lg shadow hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
                  data-testid="calculate-avsf-btn"
                >
                  Calculate Scrappage Value <ArrowRight className="ml-2 h-4 w-4" />
                </button>
                <div className="p-6 rounded-xl bg-primary/5 border-2 border-primary/20">
                  <h4 className="font-bold text-lg mb-4 text-primary">Estimated Benefits:</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center border-b pb-2">
                      <span className="text-sm text-muted-foreground">Scrap Certificate Value (4-6%)</span>
                      <span className="font-bold text-primary">{avsfResults?.scrapCert || '₹24,000-36,000'}</span>
                    </div>
                    <div className="flex justify-between items-center border-b pb-2">
                      <span className="text-sm text-muted-foreground">Road Tax Rebate (25%)</span>
                      <span className="font-bold text-primary">{avsfResults?.roadTax || '₹15,000-40,000'}</span>
                    </div>
                    <div className="flex justify-between items-center border-b pb-2">
                      <span className="text-sm text-muted-foreground">Manufacturer Discount (5%)</span>
                      <span className="font-bold text-primary">{avsfResults?.manufacturer || '₹20,000-30,000'}</span>
                    </div>
                    <div className="flex justify-between items-center border-b pb-2">
                      <span className="text-sm text-muted-foreground">Registration Fee Waived</span>
                      <span className="font-bold text-primary">{avsfResults?.registration || '₹5,000-10,000'}</span>
                    </div>
                    <div className="flex justify-between items-center pt-3">
                      <span className="font-bold text-lg">Total Benefits</span>
                      <span className="font-bold text-2xl text-primary">{avsfResults?.total || '₹64,000-116,000'}</span>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground italic">
                  *Actual benefits may vary based on state, vehicle condition, and manufacturer offers. Based on Government of India VVMP policy.
                </p>
              </CardContent>
            </Card>

            {/* Solar ROI Calculator */}
            <Card className="premium-card">
              <CardHeader>
                <div className="flex items-center justify-between mb-4">
                  <Badge className="bg-accent text-accent-foreground">Solar Calculator</Badge>
                  <Sun className="h-6 w-6 text-accent-foreground" />
                </div>
                <CardTitle className="text-2xl mb-2">Solar Farming Returns</CardTitle>
                <CardDescription>Calculate potential income from solar partnership</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold mb-2">Land Size (Acres)</label>
                  <Input 
                    type="number" 
                    placeholder="e.g., 5" 
                    className="text-lg" 
                    data-testid="land-size"
                    value={solarInputs.landSize}
                    onChange={(e) => setSolarInputs({...solarInputs, landSize: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Investment Model</label>
                  <select 
                    className="w-full p-3 border rounded-lg bg-background" 
                    data-testid="investment-model"
                    value={solarInputs.investmentModel}
                    onChange={(e) => setSolarInputs({...solarInputs, investmentModel: e.target.value})}
                  >
                    <option value="8020">80/20 Co-Investment (ESG 80%, You 20%)</option>
                    <option value="resco">RESCO (Zero Investment)</option>
                    <option value="full">Full Ownership</option>
                  </select>
                </div>
                <button
                  ref={solarButtonRef}
                  type="button"
                  className="w-full h-12 px-8 bg-accent text-accent-foreground rounded-lg font-bold text-lg shadow hover:bg-accent/90 transition-colors flex items-center justify-center gap-2"
                  data-testid="calculate-solar-btn"
                >
                  Calculate Returns <ArrowRight className="ml-2 h-4 w-4" />
                </button>
                <div className="p-6 rounded-xl bg-accent/10 border-2 border-accent/30">
                  <h4 className="font-bold text-lg mb-4 text-accent-foreground">Projected Returns:</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center border-b pb-2">
                      <span className="text-sm text-muted-foreground">Capacity</span>
                      <span className="font-bold text-accent-foreground">{solarResults?.capacity || '1.25 MW (5 acres)'}</span>
                    </div>
                    <div className="flex justify-between items-center border-b pb-2">
                      <span className="text-sm text-muted-foreground">Your Investment</span>
                      <span className="font-bold text-accent-foreground">{solarResults?.investment || '₹1.68 Cr (20%)'}</span>
                    </div>
                    <div className="flex justify-between items-center border-b pb-2">
                      <span className="text-sm text-muted-foreground">Annual Income</span>
                      <span className="font-bold text-accent-foreground">{solarResults?.annualIncome || '₹18.75 Lakhs'}</span>
                    </div>
                    <div className="flex justify-between items-center border-b pb-2">
                      <span className="text-sm text-muted-foreground">Annual ROI</span>
                      <span className="font-bold text-accent-foreground">{solarResults?.roi || '22.3%'}</span>
                    </div>
                    <div className="flex justify-between items-center border-b pb-2">
                      <span className="text-sm text-muted-foreground">Payback Period</span>
                      <span className="font-bold text-accent-foreground">{solarResults?.payback || '4.5 years'}</span>
                    </div>
                    <div className="flex justify-between items-center pt-3">
                      <span className="font-bold text-lg">25-Year Total Return</span>
                      <span className="font-bold text-xl text-accent-foreground">{solarResults?.totalReturn || '₹3.75-5 Cr'}</span>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground italic">
                  *Based on current PPA rates ₹2.5-4.5/unit. PM-KUSUM subsidy (40-60%) may further reduce investment.
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="mt-12 text-center">
            <Card className="inline-block p-6">
              <p className="text-sm text-muted-foreground mb-4">
                <strong>Want detailed calculations?</strong> Contact us for personalized projections based on your specific requirements.
              </p>
              <Button onClick={() => scrollToSection('contact')} data-testid="calculator-quote-btn">
                Get Personalized Quote <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Card>
          </div>
        </div>
      </section>

      {/* Government Policies Section */}
      <section id="policies" className="py-24 sm:py-32" data-testid="policies-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <Badge className="mb-4 text-sm px-4 py-2">Government Policies</Badge>
            <h2 className="text-4xl sm:text-5xl font-bold tracking-tight text-foreground mb-6">
              Policy Framework & Compliance
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
              Understanding the government policies that drive India's renewable and recycling sectors.
            </p>
          </div>

          <div className="grid gap-8">
            {/* VVMP Policy */}
            <Card className="premium-card">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center mb-3">
                      <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mr-4">
                        <Shield className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-2xl">VVMP - Vehicle Scrappage Policy</CardTitle>
                        <p className="text-sm text-muted-foreground">Voluntary Vehicle-Fleet Modernization Program</p>
                      </div>
                    </div>
                  </div>
                  <Badge>Gov. of India</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h4 className="font-bold mb-3">Policy Overview:</h4>
                  <p className="text-muted-foreground leading-relaxed">
                    Launched in 2021, the VVMP makes it mandatory for <strong>commercial vehicles over 15 years</strong> (from April 2023) and <strong>private vehicles over 20 years</strong> (from June 2024) to undergo fitness certification. Vehicles failing the test must be scrapped at registered facilities.
                  </p>
                </div>
                
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="p-4 rounded-lg bg-muted/50">
                    <h5 className="font-semibold mb-2">Eligible Vehicles</h5>
                    <p className="text-2xl font-bold text-primary mb-1">12 Million</p>
                    <p className="text-xs text-muted-foreground">5.1M personal + 3.4M commercial</p>
                  </div>
                  <div className="p-4 rounded-lg bg-muted/50">
                    <h5 className="font-semibold mb-2">Progress (2025)</h5>
                    <p className="text-2xl font-bold text-primary mb-1">350,500</p>
                    <p className="text-xs text-muted-foreground">Vehicles scrapped (3% of eligible)</p>
                  </div>
                  <div className="p-4 rounded-lg bg-muted/50">
                    <h5 className="font-semibold mb-2">Target (2026)</h5>
                    <p className="text-2xl font-bold text-primary mb-1">500,000+</p>
                    <p className="text-xs text-muted-foreground">Vehicles per year</p>
                  </div>
                </div>

                <div>
                  <h4 className="font-bold mb-3">Financial Incentives:</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="flex items-start space-x-3">
                      <CheckCircle2 className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold">Scrap Certificate Value</p>
                        <p className="text-sm text-muted-foreground">4-6% of ex-showroom price (₹25,000-40,000)</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <CheckCircle2 className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold">Road Tax Rebate</p>
                        <p className="text-sm text-muted-foreground">Up to 25% (personal), 15% (commercial)</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <CheckCircle2 className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold">Manufacturer Discount</p>
                        <p className="text-sm text-muted-foreground">5% or ₹20,000-25,000 on new purchase</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <CheckCircle2 className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold">Registration Fee Waiver</p>
                        <p className="text-sm text-muted-foreground">Complete waiver with scrap certificate</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
                  <p className="text-sm font-semibold text-primary mb-2">Example: Maruti Swift (₹6 Lakh ex-showroom)</p>
                  <p className="text-sm text-muted-foreground">
                    Total benefits: Scrap value ₹36,000 + Road tax rebate ₹40,000 + Manufacturer discount ₹30,000 + Registration waiver ₹9,000 = <strong className="text-primary">₹1,15,000 total savings</strong>
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* PM-KUSUM Scheme */}
            <Card className="premium-card">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center mb-3">
                      <div className="h-12 w-12 rounded-xl bg-accent flex items-center justify-center mr-4">
                        <Sun className="h-6 w-6 text-accent-foreground" />
                      </div>
                      <div>
                        <CardTitle className="text-2xl">PM-KUSUM Scheme</CardTitle>
                        <p className="text-sm text-muted-foreground">Pradhan Mantri Kisan Urja Suraksha evam Utthaan Mahabhiyan</p>
                      </div>
                    </div>
                  </div>
                  <Badge className="bg-accent text-accent-foreground">Solar Subsidy</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h4 className="font-bold mb-3">Scheme Overview:</h4>
                  <p className="text-muted-foreground leading-relaxed">
                    PM-KUSUM provides <strong>40-60% subsidy</strong> for setting up solar farms on agricultural land. The scheme aims to add <strong>30.8 GW solar capacity</strong> and benefit 20 lakh farmers by 2026.
                  </p>
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                  <div className="p-4 rounded-lg bg-accent/10">
                    <h5 className="font-semibold mb-2">Component A</h5>
                    <p className="text-xl font-bold text-accent-foreground mb-1">10 GW</p>
                    <p className="text-xs text-muted-foreground">Grid-connected solar power plants</p>
                  </div>
                  <div className="p-4 rounded-lg bg-accent/10">
                    <h5 className="font-semibold mb-2">Component B</h5>
                    <p className="text-xl font-bold text-accent-foreground mb-1">20 Lakh</p>
                    <p className="text-xs text-muted-foreground">Solar pumps (17.5 GW)</p>
                  </div>
                  <div className="p-4 rounded-lg bg-accent/10">
                    <h5 className="font-semibold mb-2">Component C</h5>
                    <p className="text-xl font-bold text-accent-foreground mb-1">15 Lakh</p>
                    <p className="text-xs text-muted-foreground">Grid-connected pumps (10.5 GW)</p>
                  </div>
                </div>

                <div>
                  <h4 className="font-bold mb-3">Financial Support:</h4>
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <CheckCircle2 className="h-5 w-5 text-accent-foreground mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold">Central Financial Assistance (CFA)</p>
                        <p className="text-sm text-muted-foreground">30% subsidy from central government</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <CheckCircle2 className="h-5 w-5 text-accent-foreground mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold">State Subsidy</p>
                        <p className="text-sm text-muted-foreground">10-30% additional subsidy (state-dependent)</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <CheckCircle2 className="h-5 w-5 text-accent-foreground mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold">Concessional Bank Loans</p>
                        <p className="text-sm text-muted-foreground">Remaining 30-60% at subsidized interest rates</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-lg bg-accent/10 border border-accent/30">
                  <p className="text-sm font-semibold text-accent-foreground mb-2">Example: 1 MW Solar Farm (₹4.2 Crore total cost)</p>
                  <div className="space-y-1 text-sm text-muted-foreground">
                    <p>• Central Subsidy (30%): <strong>₹1.26 Crore</strong></p>
                    <p>• State Subsidy (10-30%): <strong>₹42 Lakh - ₹1.26 Crore</strong></p>
                    <p>• Effective Investment: <strong>₹1.68-2.52 Crore</strong> (60-40% reduction)</p>
                    <p className="pt-2 font-semibold text-accent-foreground">With subsidies, payback reduces to <strong>3-5 years</strong> from 7-9 years</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* BRSR Compliance */}
            <Card className="premium-card">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center mb-3">
                      <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mr-4">
                        <FileCheck className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-2xl">BRSR Core - ESG Reporting</CardTitle>
                        <p className="text-sm text-muted-foreground">Business Responsibility & Sustainability Reporting</p>
                      </div>
                    </div>
                  </div>
                  <Badge>SEBI Mandatory</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h4 className="font-bold mb-3">Compliance Framework:</h4>
                  <p className="text-muted-foreground leading-relaxed">
                    SEBI mandates <strong>BRSR Core reporting</strong> for top 1,000 listed companies from FY 2023-24, with <strong>third-party assurance required from FY 2026-27</strong>. Top 250-500 companies will require ESG reporting progressively.
                  </p>
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                  <div className="p-4 rounded-lg bg-muted/50">
                    <h5 className="font-semibold mb-2">Top 1,000</h5>
                    <p className="text-xl font-bold text-primary mb-1">Mandatory</p>
                    <p className="text-xs text-muted-foreground">BRSR Core from FY 2023-24</p>
                  </div>
                  <div className="p-4 rounded-lg bg-muted/50">
                    <h5 className="font-semibold mb-2">Top 250</h5>
                    <p className="text-xl font-bold text-primary mb-1">FY 2025-26</p>
                    <p className="text-xs text-muted-foreground">ESG reporting mandated</p>
                  </div>
                  <div className="p-4 rounded-lg bg-muted/50">
                    <h5 className="font-semibold mb-2">Assurance</h5>
                    <p className="text-xl font-bold text-primary mb-1">FY 2026-27</p>
                    <p className="text-xs text-muted-foreground">Third-party verification</p>
                  </div>
                </div>

                <div>
                  <h4 className="font-bold mb-3">Key ESG Parameters:</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="p-3 rounded-lg border">
                      <p className="font-semibold mb-2">Environmental</p>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        <li>• GHG emissions reduction</li>
                        <li>• Waste management & recycling</li>
                        <li>• Water conservation</li>
                      </ul>
                    </div>
                    <div className="p-3 rounded-lg border">
                      <p className="font-semibold mb-2">Social</p>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        <li>• Employee welfare</li>
                        <li>• Community development</li>
                        <li>• Diversity & inclusion</li>
                      </ul>
                    </div>
                    <div className="p-3 rounded-lg border">
                      <p className="font-semibold mb-2">Governance</p>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        <li>• Board diversity</li>
                        <li>• Ethical business practices</li>
                        <li>• Compliance & transparency</li>
                      </ul>
                    </div>
                    <div className="p-3 rounded-lg border">
                      <p className="font-semibold mb-2">Disclosure</p>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        <li>• Quantitative metrics</li>
                        <li>• Third-party assurance</li>
                        <li>• Stakeholder engagement</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Process Section - Enhanced */}
      <section id="process" className="py-24 sm:py-32 bg-muted/30" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <Badge className="mb-4 text-sm px-4 py-2">Our Process</Badge>
            <h2 className="text-4xl sm:text-5xl font-bold tracking-tight text-foreground mb-6" data-testid="process-title">
              How We Work With You
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto" data-testid="process-description">
              Our systematic approach ensures compliance, safety, and maximum value at every step of the journey.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0 }}
              className="relative"
              data-testid="process-step-1"
            >
              <Card className="text-center h-full hover:shadow-xl transition-shadow border-t-4 border-t-primary">
                <CardContent className="pt-8 pb-8">
                  <div className="h-20 w-20 rounded-full bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-6 text-3xl font-bold shadow-lg">
                    1
                  </div>
                  <h3 className="font-bold text-xl mb-3">Initial Assessment</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">Comprehensive evaluation of your requirements with detailed compliance and feasibility checks</p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="relative"
              data-testid="process-step-2"
            >
              <Card className="text-center h-full hover:shadow-xl transition-shadow border-t-4 border-t-primary">
                <CardContent className="pt-8 pb-8">
                  <div className="h-20 w-20 rounded-full bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-6 text-3xl font-bold shadow-lg">
                    2
                  </div>
                  <h3 className="font-bold text-xl mb-3">Partnership Setup</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">Complete documentation, regulatory filings, and transparent partnership agreements</p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="relative"
              data-testid="process-step-3"
            >
              <Card className="text-center h-full hover:shadow-xl transition-shadow border-t-4 border-t-primary">
                <CardContent className="pt-8 pb-8">
                  <div className="h-20 w-20 rounded-full bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-6 text-3xl font-bold shadow-lg">
                    3
                  </div>
                  <h3 className="font-bold text-xl mb-3">Implementation</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">Professional execution with modern equipment, safety protocols, and quality assurance</p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="relative"
              data-testid="process-step-4"
            >
              <Card className="text-center h-full hover:shadow-xl transition-shadow border-t-4 border-t-primary">
                <CardContent className="pt-8 pb-8">
                  <div className="h-20 w-20 rounded-full bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-6 text-3xl font-bold shadow-lg">
                    4
                  </div>
                  <h3 className="font-bold text-xl mb-3">Ongoing Support</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">Continuous monitoring, reporting, and certification with dedicated support</p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Removed duplicate 'Why Choose Us' section - replaced by 'Why ESG Solutions' above */}

      {/* Removed duplicate old 'About Section' - replaced by 'About / Value Proposition' above */}

      {/* Leadership Team Section */}
      <section id="leadership" className="py-24 sm:py-32" data-testid="leadership-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <Badge className="mb-4 text-sm px-4 py-2">Leadership Team</Badge>
            <h2 className="text-4xl sm:text-5xl font-bold tracking-tight text-foreground mb-6">
              World-Class Leadership Team
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
              Our leadership combines international business acumen, entrepreneurial excellence, and deep ESG expertise—bringing together 65+ years of combined experience.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-10 max-w-7xl mx-auto mb-16">
            {/* Ratan Medi */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Card className="leadership-card premium-card h-full border-2 border-primary/20 bg-gradient-to-br from-card via-card to-primary/5" data-testid="founder-ravneet">
                <CardContent className="p-8">
                  <div className="flex flex-col items-center text-center mb-6">
                    <div className="relative mb-6">
                      <div className="absolute inset-0 bg-primary/20 rounded-3xl blur-xl"></div>
                      <div className="relative h-32 w-32 rounded-3xl bg-gradient-to-br from-primary via-primary to-primary/80 flex items-center justify-center text-primary-foreground text-4xl font-bold shadow-2xl">
                        RSM
                      </div>
                    </div>
                    <h3 className="text-3xl font-bold mb-2">Ravneet Singh Medi</h3>
                    <p className="text-xl text-primary font-bold mb-3">ESG Expert & Advisor</p>
                    <div className="flex items-center text-base text-muted-foreground bg-accent/20 px-4 py-2 rounded-full">
                      <Award className="h-5 w-5 mr-2 text-primary" />
                      <span>Senior Consultant & Analyst</span>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <p className="text-base text-muted-foreground leading-relaxed text-center">
                      25+ years of expertise as Senior Consultant and Analyst, specializing in setting up and scaling major industrial projects across India.
                    </p>
                    <div className="pt-4 border-t-2 border-primary/20">
                      <h4 className="font-bold text-sm mb-3 text-center text-foreground">Key Expertise:</h4>
                      <div className="flex flex-wrap gap-2 justify-center">
                        <Badge className="text-xs bg-primary/90 text-primary-foreground">ESG Compliance</Badge>
                        <Badge className="text-xs bg-primary/90 text-primary-foreground">Industry Setup</Badge>
                        <Badge className="text-xs bg-primary/90 text-primary-foreground">Regulatory Affairs</Badge>
                        <Badge className="text-xs bg-primary/90 text-primary-foreground">Strategic Advisory</Badge>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Sachin Madan */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <Card className="leadership-card premium-card h-full border-2 border-primary/20 bg-gradient-to-br from-card via-card to-primary/5" data-testid="founder-sachin">
                <CardContent className="p-8">
                  <div className="flex flex-col items-center text-center mb-6">
                    <div className="relative mb-6">
                      <div className="absolute inset-0 bg-primary/20 rounded-3xl blur-xl"></div>
                      <div className="relative h-32 w-32 rounded-3xl bg-gradient-to-br from-primary via-primary to-primary/80 flex items-center justify-center text-primary-foreground text-4xl font-bold shadow-2xl">
                        SM
                      </div>
                    </div>
                    <h3 className="text-3xl font-bold mb-2">Sachin Madan</h3>
                    <p className="text-xl text-primary font-bold mb-3">Co-Founder & Director</p>
                    <div className="flex items-center text-base text-muted-foreground bg-accent/20 px-4 py-2 rounded-full">
                      <Building2 className="h-5 w-5 mr-2 text-primary" />
                      <span>Serial Entrepreneur</span>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <p className="text-base text-muted-foreground leading-relaxed text-center">
                      Accomplished Indian businessman with proven track record of successfully establishing and operating multiple businesses across diverse sectors.
                    </p>
                    <div className="pt-4 border-t-2 border-primary/20">
                      <h4 className="font-bold text-sm mb-3 text-center text-foreground">Key Expertise:</h4>
                      <div className="flex flex-wrap gap-2 justify-center">
                        <Badge className="text-xs bg-primary/90 text-primary-foreground">Business Development</Badge>
                        <Badge className="text-xs bg-primary/90 text-primary-foreground">Local Market Expertise</Badge>
                        <Badge className="text-xs bg-primary/90 text-primary-foreground">Multi-Industry Experience</Badge>
                        <Badge className="text-xs bg-primary/90 text-primary-foreground">Entrepreneurship</Badge>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Kapil Dhamija */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Card className="leadership-card premium-card h-full border-2 border-primary/20 bg-gradient-to-br from-card via-card to-primary/5" data-testid="founder-kapil">
                <CardContent className="p-8">
                  <div className="flex flex-col items-center text-center mb-6">
                    <div className="relative mb-6">
                      <div className="absolute inset-0 bg-primary/20 rounded-3xl blur-xl"></div>
                      <div className="relative h-32 w-32 rounded-3xl bg-gradient-to-br from-primary via-primary to-primary/80 flex items-center justify-center text-primary-foreground text-4xl font-bold shadow-2xl">
                        KD
                      </div>
                    </div>
                    <h3 className="text-3xl font-bold mb-2">Kapil Dhamija</h3>
                    <p className="text-xl text-primary font-bold mb-3">Co-Founder & Director</p>
                    <div className="flex items-center text-base text-muted-foreground bg-accent/20 px-4 py-2 rounded-full">
                      <Globe className="h-5 w-5 mr-2 text-primary" />
                      <span>Australian Business Background</span>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <p className="text-base text-muted-foreground leading-relaxed text-center">
                      Brings 20+ years of international business experience from Australia, providing global perspective and best practices to ESG Solutions' operations.
                    </p>
                    <div className="pt-4 border-t-2 border-primary/20">
                      <h4 className="font-bold text-sm mb-3 text-center text-foreground">Key Expertise:</h4>
                      <div className="flex flex-wrap gap-2 justify-center">
                        <Badge className="text-xs bg-primary/90 text-primary-foreground">International Business</Badge>
                        <Badge className="text-xs bg-primary/90 text-primary-foreground">Strategic Planning</Badge>
                        <Badge className="text-xs bg-primary/90 text-primary-foreground">Operations Management</Badge>
                        <Badge className="text-xs bg-primary/90 text-primary-foreground">Global Partnerships</Badge>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Additional Leadership Stats */}
          <div className="grid md:grid-cols-5 gap-6 text-center">
            <div>
              <div className="text-4xl font-bold text-primary mb-2 metric-value premium-number">65+</div>
              <div className="text-sm text-muted-foreground">Years Combined<br/>Experience</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-primary mb-2 metric-value premium-number">25+</div>
              <div className="text-sm text-muted-foreground">Years ESG<br/>Expertise</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-primary mb-2 metric-value premium-number">Multiple</div>
              <div className="text-sm text-muted-foreground">Industries<br/>Established</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-primary mb-2 metric-value premium-number">2</div>
              <div className="text-sm text-muted-foreground">Countries<br/>Business Reach</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-primary mb-2 metric-value premium-number">100%</div>
              <div className="text-sm text-muted-foreground">Compliance<br/>Focus</div>
            </div>
          </div>
        </div>
      </section>

      {/* Removed duplicate old Market Opportunity section */}

      {/* Case Studies Section */}
      <section className="py-24 sm:py-32" data-testid="casestudies-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <Badge className="mb-4 text-sm px-4 py-2">Success Stories</Badge>
            <h2 className="text-4xl sm:text-5xl font-bold tracking-tight text-foreground mb-6">
              Proven Results & ROI
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
              Real examples showcasing the value we create for our partners and the environment.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Case Study 1 */}
            <Card className="case-study-card premium-card">
              <CardHeader>
                <div className="flex items-center justify-between mb-4">
                  <Badge>AVSF Case Study</Badge>
                  <span className="text-sm text-muted-foreground">Q4 2023</span>
                </div>
                <CardTitle className="text-2xl mb-2">Commercial Fleet Scrapping Program</CardTitle>
                <CardDescription className="text-base">Large logistics company fleet replacement initiative</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 rounded-lg bg-muted/50">
                    <div className="text-3xl font-bold text-primary mb-1 premium-number">250</div>
                    <div className="text-xs text-muted-foreground">Vehicles Processed</div>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-muted/50">
                    <div className="text-3xl font-bold text-primary mb-1 premium-number">85%</div>
                    <div className="text-xs text-muted-foreground">Material Recovery</div>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-muted/50">
                    <div className="text-3xl font-bold text-primary mb-1 premium-number">30</div>
                    <div className="text-xs text-muted-foreground">Days Completion</div>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-muted/50">
                    <div className="text-3xl font-bold text-primary mb-1 premium-number">100%</div>
                    <div className="text-xs text-muted-foreground">Compliance Rate</div>
                  </div>
                </div>
                <div className="pt-4 border-t">
                  <h5 className="font-semibold mb-2 text-sm">Key Outcomes:</h5>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start">
                      <CheckCircle2 className="h-4 w-4 text-primary mr-2 mt-0.5 flex-shrink-0" />
                      <span>Complete documentation for all vehicles with zero compliance issues</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="h-4 w-4 text-primary mr-2 mt-0.5 flex-shrink-0" />
                      <span>Materials recovered valued at ₹1.2 Cr, shared with client</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="h-4 w-4 text-primary mr-2 mt-0.5 flex-shrink-0" />
                      <span>Client received government incentives worth ₹85 lakhs</span>
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Case Study 2 - Solar Partnership Projection */}
            <Card className="case-study-card premium-card border-dashed">
              <CardHeader>
                <div className="flex items-center justify-between mb-4">
                  <Badge className="bg-accent text-accent-foreground">Solar Partnership Model</Badge>
                  <span className="text-sm text-muted-foreground">Projected</span>
                </div>
                <CardTitle className="text-2xl mb-2">5-Acre Solar Farm Partnership</CardTitle>
                <CardDescription className="text-base">Typical landowner co-investment model projection</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 rounded-lg bg-accent/10">
                    <div className="text-3xl font-bold text-accent-foreground mb-1 premium-number">1 MW</div>
                    <div className="text-xs text-muted-foreground">Generation Capacity</div>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-accent/10">
                    <div className="text-3xl font-bold text-accent-foreground mb-1 premium-number">₹4.2 Cr</div>
                    <div className="text-xs text-muted-foreground">Total Investment</div>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-accent/10">
                    <div className="text-3xl font-bold text-accent-foreground mb-1 premium-number">18-22%</div>
                    <div className="text-xs text-muted-foreground">Projected IRR</div>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-accent/10">
                    <div className="text-3xl font-bold text-accent-foreground mb-1 premium-number">25 Yrs</div>
                    <div className="text-xs text-muted-foreground">PPA Duration</div>
                  </div>
                </div>
                <div className="pt-4 border-t">
                  <h5 className="font-semibold mb-2 text-sm">Partnership Benefits:</h5>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start">
                      <CheckCircle2 className="h-4 w-4 text-accent-foreground mr-2 mt-0.5 flex-shrink-0" />
                      <span>Landowner co-invests 20%, ESG Solutions 80%</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="h-4 w-4 text-accent-foreground mr-2 mt-0.5 flex-shrink-0" />
                      <span>Guaranteed power purchase agreement with industrial facility</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="h-4 w-4 text-accent-foreground mr-2 mt-0.5 flex-shrink-0" />
                      <span>Monthly income distribution based on equity share</span>
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 sm:py-32 bg-muted/30" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <Badge className="mb-4 text-sm px-4 py-2">Client Testimonials</Badge>
            <h2 className="text-4xl sm:text-5xl font-bold tracking-tight text-foreground mb-6">
              Trusted by Industry Leaders
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
              Hear from our partners about their experience working with ESG Solutions.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Testimonial 1 */}
            <Card className="testimonial-card relative">
              <CardContent className="pt-8 pb-8">
                <Quote className="h-10 w-10 text-primary/20 mb-4" />
                <p className="text-base text-muted-foreground mb-6 leading-relaxed italic">
                  "ESG Solutions handled our fleet scrapping with complete professionalism. The process was transparent, compliant, and delivered better value than we expected."
                </p>
                <div className="flex items-center space-x-4">
                  <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Building2 className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <div className="font-semibold">Rajesh Kumar</div>
                    <div className="text-sm text-muted-foreground">Fleet Manager, Major Logistics Co.</div>
                  </div>
                </div>
                <div className="mt-4 flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-primary text-primary" />
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Testimonial 2 */}
            <Card className="testimonial-card relative">
              <CardContent className="pt-8 pb-8">
                <Quote className="h-10 w-10 text-primary/20 mb-4" />
                <p className="text-base text-muted-foreground mb-6 leading-relaxed italic">
                  "The team's expertise in compliance and documentation made our vehicle retirement program seamless. Highly recommended for their professionalism."
                </p>
                <div className="flex items-center space-x-4">
                  <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Building2 className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <div className="font-semibold">Priya Sharma</div>
                    <div className="text-sm text-muted-foreground">Operations Director, Transport Solutions</div>
                  </div>
                </div>
                <div className="mt-4 flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-primary text-primary" />
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Testimonial 3 */}
            <Card className="testimonial-card relative">
              <CardContent className="pt-8 pb-8">
                <Quote className="h-10 w-10 text-primary/20 mb-4" />
                <p className="text-base text-muted-foreground mb-6 leading-relaxed italic">
                  "Their systematic approach and modern facility gave us complete confidence. The recovery rates exceeded industry standards."
                </p>
                <div className="flex items-center space-x-4">
                  <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Building2 className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <div className="font-semibold">Amit Patel</div>
                    <div className="text-sm text-muted-foreground">Sustainability Head, Auto Manufacturer</div>
                  </div>
                </div>
                <div className="mt-4 flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-primary text-primary" />
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Investment Opportunity CTA */}
      <section className="py-24 sm:py-32 premium-gradient-1" data-testid="investment-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Badge className="mb-6 text-sm px-4 py-2">Investment Opportunity</Badge>
            <h2 className="text-4xl sm:text-5xl font-bold tracking-tight text-foreground mb-6">
              Partner With India's Renewable Future
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground mb-12 max-w-3xl mx-auto leading-relaxed">
              Join us in capturing the massive market opportunity in vehicle recycling and solar energy. With experienced leadership, proven business model, and strong government tailwinds, ESG Solutions offers an attractive investment proposition.
            </p>

            <div className="grid md:grid-cols-4 gap-8 mb-12">
              <div className="text-center">
                <Rocket className="h-12 w-12 text-primary mx-auto mb-3" />
                <div className="text-3xl font-bold text-primary mb-2 premium-number">$27.5 Bn</div>
                <div className="text-sm text-muted-foreground">AVSF Market (2032)</div>
              </div>
              <div className="text-center">
                <LineChart className="h-12 w-12 text-primary mx-auto mb-3" />
                <div className="text-3xl font-bold text-primary mb-2 premium-number">13.7%</div>
                <div className="text-sm text-muted-foreground">Market CAGR</div>
              </div>
              <div className="text-center">
                <Target className="h-12 w-12 text-primary mx-auto mb-3" />
                <div className="text-3xl font-bold text-primary mb-2 premium-number">172 GW</div>
                <div className="text-sm text-muted-foreground">Solar Pipeline</div>
              </div>
              <div className="text-center">
                <Award className="h-12 w-12 text-primary mx-auto mb-3" />
                <div className="text-3xl font-bold text-primary mb-2 premium-number">65+</div>
                <div className="text-sm text-muted-foreground">Years Experience</div>
              </div>
            </div>

            <div className="flex flex-wrap justify-center gap-6">
              <Button 
                size="lg" 
                className="investment-cta shadow-2xl text-lg px-10 py-7"
                onClick={() => scrollToSection('contact')}
                data-testid="investor-meeting-btn"
              >
                Schedule Investor Meeting <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="shadow-lg text-lg px-10 py-7"
                onClick={() => scrollToSection('contact')}
                data-testid="business-plan-btn"
              >
                Download Business Plan
              </Button>
            </div>

            <p className="text-sm text-muted-foreground mt-8">
              For detailed investor presentations and financials, contact us directly
            </p>
          </motion.div>
        </div>
      </section>


      {/* Closing CTA Section - Final Conversion Push */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-primary/5 via-accent/5 to-primary/5 relative overflow-hidden" data-testid="closing-cta-section">
        {/* Subtle Background Texture */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%232c5a4b' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <Badge className="mb-6 text-sm px-5 py-2.5" data-testid="closing-cta-badge">
              Ready to Get Started?
            </Badge>
            
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-foreground mb-8" data-testid="closing-cta-heading">
              Let's Build the Future Sustainably
            </h2>
            
            <p className="text-xl md:text-2xl text-muted-foreground max-w-4xl mx-auto mb-12 leading-relaxed" data-testid="closing-cta-text">
              Whether you are a landowner, investor, or strategic partner, ESG Solutions is ready to create long-term value through renewable infrastructure.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-5 justify-center items-center mb-10">
              <Button 
                size="lg" 
                className="text-lg px-10 py-6 shadow-xl hover:shadow-2xl font-semibold h-auto hover:scale-[1.02] transition-all duration-300"
                onClick={() => scrollToSection('contact')}
                data-testid="closing-cta-partner"
              >
                Partner With Us <Handshake className="ml-2 h-6 w-6" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="text-lg px-10 py-6 shadow-lg hover:shadow-xl font-semibold border-2 h-auto hover:scale-[1.02] transition-all duration-300"
                onClick={() => scrollToSection('contact')}
                data-testid="closing-cta-schedule"
              >
                Schedule a Call <Phone className="ml-2 h-6 w-6" />
              </Button>
            </div>
            
            {/* Trust Reinforcement */}
            <div className="flex flex-wrap justify-center gap-8 pt-8 border-t border-border/50">
              <div className="flex items-center gap-3" data-testid="closing-trust-1">
                <Shield className="h-6 w-6 text-primary" />
                <span className="text-sm font-medium text-muted-foreground">Government Compliant</span>
              </div>
              <div className="flex items-center gap-3" data-testid="closing-trust-2">
                <Award className="h-6 w-6 text-primary" />
                <span className="text-sm font-medium text-muted-foreground">65+ Years Experience</span>
              </div>
              <div className="flex items-center gap-3" data-testid="closing-trust-3">
                <Zap className="h-6 w-6 text-primary" />
                <span className="text-sm font-medium text-muted-foreground">Proven Track Record</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Section - Enhanced */}
      <section id="contact" className="py-24 sm:py-32" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 text-sm px-4 py-2">Get In Touch</Badge>
            <h2 className="text-4xl sm:text-5xl font-bold tracking-tight text-foreground mb-6" data-testid="contact-title">
              Let's Build Something Together
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto" data-testid="contact-description">
              Ready to start your environmental sustainability journey? Contact us for a consultation or to explore partnership opportunities.
            </p>
          </div>

          <div className="grid lg:grid-cols-5 gap-12">
            <div className="lg:col-span-2 space-y-8">
              <div className="flex items-start" data-testid="contact-info-email">
                <div className="h-14 w-14 rounded-xl bg-primary/10 flex items-center justify-center mr-4 flex-shrink-0">
                  <Mail className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-2">Email Us</h4>
                  <a href="mailto:info@esg-solutions.in" className="text-base text-muted-foreground hover:text-primary transition-colors">
                    info@esg-solutions.in
                  </a>
                </div>
              </div>

              <div className="flex items-start" data-testid="contact-info-phone">
                <div className="h-14 w-14 rounded-xl bg-primary/10 flex items-center justify-center mr-4 flex-shrink-0">
                  <Phone className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-2">Call Us</h4>
                  <a href="tel:1300873374" className="text-base text-muted-foreground hover:text-primary transition-colors">
                    1300 TRY ESG
                  </a>
                </div>
              </div>

              <div className="flex items-start" data-testid="contact-info-location">
                <div className="h-14 w-14 rounded-xl bg-primary/10 flex items-center justify-center mr-4 flex-shrink-0">
                  <MapPin className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-2">Visit Us</h4>
                  <p className="text-base text-muted-foreground">HQ: Gurugram, Haryana</p>
                  <p className="text-base text-muted-foreground">Facility: Panipat, Haryana</p>
                </div>
              </div>

              <div className="pt-8 border-t">
                <h4 className="font-bold text-lg mb-4">Business Hours</h4>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <div className="flex justify-between">
                    <span>Monday - Saturday:</span>
                    <span className="font-medium">9:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Sunday:</span>
                    <span className="font-medium">Closed</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-3">
              <Card className="shadow-2xl border-2" data-testid="contact-form-card">
                <CardHeader className="pb-6">
                  <CardTitle className="text-2xl" data-testid="form-title">Send us a Message</CardTitle>
                  <CardDescription className="text-base" data-testid="form-description">
                    Fill out the form below and we'll get back to you within 24 hours.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Honeypot field */}
                    <input
                      type="text"
                      name="honeypot"
                      value={formData.honeypot}
                      onChange={handleInputChange}
                      className="honeypot-field"
                      tabIndex="-1"
                      autoComplete="off"
                    />

                    <div className="grid md:grid-cols-2 gap-6">
                      <div data-testid="form-field-name">
                        <label htmlFor="name" className="block text-sm font-semibold mb-2">
                          Full Name *
                        </label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          placeholder="John Doe"
                          className="h-12"
                          data-testid="input-name"
                        />
                      </div>

                      <div data-testid="form-field-email">
                        <label htmlFor="email" className="block text-sm font-semibold mb-2">
                          Email Address *
                        </label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          placeholder="john@example.com"
                          className="h-12"
                          data-testid="input-email"
                        />
                      </div>
                    </div>

                    <div data-testid="form-field-phone">
                      <label htmlFor="phone" className="block text-sm font-semibold mb-2">
                        Phone Number
                      </label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="1300 TRY ESG"
                        className="h-12"
                        data-testid="input-phone"
                      />
                    </div>

                    <div data-testid="form-field-subject">
                      <label htmlFor="subject" className="block text-sm font-semibold mb-2">
                        Subject *
                      </label>
                      <Input
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        required
                        placeholder="How can we help you?"
                        className="h-12"
                        data-testid="input-subject"
                      />
                    </div>

                    <div data-testid="form-field-message">
                      <label htmlFor="message" className="block text-sm font-semibold mb-2">
                        Message *
                      </label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        rows={5}
                        placeholder="Tell us more about your requirements, partnership interests, or any questions you have..."
                        data-testid="input-message"
                      />
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      className="w-full text-lg py-6 shadow-lg"
                      disabled={isSubmitting}
                      data-testid="submit-button"
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'} <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer - Enhanced */}
      <footer className="bg-foreground text-background py-16" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div className="md:col-span-2">
              <div className="flex items-center space-x-3 mb-6">
                <div className="h-10 w-10 rounded-lg bg-background flex items-center justify-center">
                  <Leaf className="h-6 w-6 text-foreground" />
                </div>
                <div>
                  <span className="text-xl font-bold">ESG Solutions</span>
                  <p className="text-xs text-background/70">Renewable Industry Leaders</p>
                </div>
              </div>
              <p className="text-base text-background/80 mb-6 leading-relaxed max-w-md" data-testid="footer-tagline">
                Building India's renewable future, one partnership at a time. Creating sustainable value through innovative environmental solutions.
              </p>
              <p className="text-sm text-background/60" data-testid="footer-copyright">
                © 2024 ESG Solutions. All rights reserved.
              </p>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-6">Quick Links</h4>
              <ul className="space-y-3">
                <li><button onClick={() => scrollToSection('home')} className="text-background/70 hover:text-background transition-colors text-base" data-testid="footer-nav-home">Home</button></li>
                <li><button onClick={() => scrollToSection('services')} className="text-background/70 hover:text-background transition-colors text-base" data-testid="footer-nav-services">Services</button></li>
                <li><button onClick={() => scrollToSection('process')} className="text-background/70 hover:text-background transition-colors text-base" data-testid="footer-nav-process">Process</button></li>
                <li><button onClick={() => scrollToSection('about')} className="text-background/70 hover:text-background transition-colors text-base" data-testid="footer-nav-about">About</button></li>
                <li><button onClick={() => scrollToSection('contact')} className="text-background/70 hover:text-background transition-colors text-base" data-testid="footer-nav-contact">Contact</button></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-6">Our Services</h4>
              <ul className="space-y-3 text-base">
                <li className="text-background/70">Vehicle Scrap Facility</li>
                <li className="text-background/70">Solar Partnerships</li>
                <li className="text-background/70">Water Management</li>
                <li className="text-background/70">Air Quality Control</li>
                <li className="text-background/70">Waste Processing</li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-background/20">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-sm text-background/60 mb-4 md:mb-0">
                Headquartered in Gurugram • AVSF Facility in Panipat
              </p>
              <div className="flex items-center space-x-6">
                <a href="mailto:info@esg-solutions.in" className="text-sm text-background/70 hover:text-background transition-colors">Email</a>
                <a href="tel:1300873374" className="text-sm text-background/70 hover:text-background transition-colors">Call</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
      
      {/* Scroll to Top Button - Ultra Prominent */}
      {showScrollTop && (
        <div className="fixed bottom-20 right-4 z-[9999]" style={{ isolation: 'isolate' }}>
          <Button
            onClick={scrollToTop}
            className="h-16 px-6 rounded-full shadow-[0_8px_30px_rgba(44,90,75,0.6)] hover:shadow-[0_12px_40px_rgba(44,90,75,0.8)] transition-all duration-300 hover:scale-110 active:scale-95 flex items-center gap-3 text-lg font-bold bg-primary text-primary-foreground animate-pulse-subtle"
            data-testid="scroll-to-top-btn"
            aria-label="Scroll to top"
            style={{ 
              border: '3px solid white',
              boxShadow: '0 8px 30px rgba(44, 90, 75, 0.7), 0 0 0 4px rgba(255, 255, 255, 0.4)'
            }}
          >
            <ArrowUp className="h-6 w-6" />
            <span className="font-bold">Top</span>
          </Button>
        </div>
      )}
      
      {/* Toast Notifications */}
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;